import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { callApi } from "../../../lib/apiCaller";

// Constants
export const ADDRESS_LABELS = {
  HOME: "home",
  WORK: "work",
  OTHER: "other",
};

export const GEOLOCATION_ERRORS = {
  PERMISSION_DENIED: "PERMISSION_DENIED",
  POSITION_UNAVAILABLE: "POSITION_UNAVAILABLE",
  TIMEOUT: "TIMEOUT",
  NOT_SUPPORTED: "NOT_SUPPORTED",
  UNKNOWN: "UNKNOWN",
};

// Async Thunks

// Fetch all customer addresses
export const fetchAddresses = createAsyncThunk(
  "address/fetchAll",
  async (payload, ThunkAPI) => {
    try {
      const authState = ThunkAPI.getState().auth;
      const token = authState.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const profile = authState?.Userprofile || {};
      const fallbackCustomerId =
        profile?.customerId || profile?._id || profile?.id || undefined;
      const customerId =
        (typeof payload === "object" && payload?.customerId) ||
        fallbackCustomerId;

      const params = customerId ? { customerId } : undefined;

      const res = await callApi({
        key: "FETCH_CUSTOMER_ADDRESS",
        method: "GET",
        params,
        auth: true,
      });
      return res.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to fetch addresses",
      );
    }
  },
);

// Create new address
export const createAddress = createAsyncThunk(
  "address/create",
  async (addressData, ThunkAPI) => {
    try {
      const authState = ThunkAPI.getState().auth;
      const token = authState.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const profile = authState?.Userprofile || {};
      const fallbackCustomerId =
        profile?.customerId || profile?._id || profile?.id || undefined;

      const normalizedAddress = addressData?.address || addressData;
      const customerId =
        addressData?.customerId || addressData?.id || fallbackCustomerId;

      const payload = customerId
        ? {
            id: customerId,

            address: normalizedAddress,
          }
        : {
            address: normalizedAddress,
          };

      const res = await callApi({
        key: "CREATE_CUSTOMER_ADDRESS",
        method: "POST",
        body: payload,
        auth: true,
      });

      return res.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to create address",
      );
    }
  },
);

// Update existing address
export const updateAddress = createAsyncThunk(
  "address/update",
  async ({ addressId, addressData }, ThunkAPI) => {
    try {
      const token = ThunkAPI.getState().auth.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const res = await callApi({
        key: "UPDATE_CUSTOMER_ADDRESS",
        method: "PUT",
        pathParam: addressId,
        body: { address: addressData },
        auth: true,
      });

      return res.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to update address",
      );
    }
  },
);

// Delete address
export const deleteAddress = createAsyncThunk(
  "address/delete",
  async (payload, ThunkAPI) => {
    try {
      const authState = ThunkAPI.getState().auth;
      const token = authState.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const normalizedAddressId =
        typeof payload === "string" || typeof payload === "number"
          ? String(payload)
          : payload?.addressId;

      if (!normalizedAddressId) {
        return ThunkAPI.rejectWithValue("Address ID is required");
      }

      const profile = authState?.Userprofile || {};
      const fallbackCustomerId =
        profile?.customerId || profile?._id || profile?.id || undefined;
      const customerId = payload?.customerId || fallbackCustomerId;

      const params = { addressId: normalizedAddressId };
      if (customerId) {
        params.customerId = customerId;
      }

      const res = await callApi({
        key: "DELETE_CUSTOMER_ADDRESS",
        method: "DELETE",
        params,
        auth: true,
      });

      return { ...res.data, deletedId: normalizedAddressId };
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to delete address",
      );
    }
  },
);

// Set default address
export const setDefaultAddress = createAsyncThunk(
  "address/setDefault",
  async (payload, ThunkAPI) => {
    try {
      const authState = ThunkAPI.getState().auth;
      const token = authState.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const normalizedAddressId =
        typeof payload === "string" || typeof payload === "number"
          ? String(payload)
          : payload?.addressId;

      if (!normalizedAddressId) {
        return ThunkAPI.rejectWithValue("Address ID is required");
      }

      const profile = authState?.Userprofile || {};
      const fallbackCustomerId =
        profile?.customerId || profile?._id || profile?.id || undefined;
      const customerId = payload?.customerId || fallbackCustomerId;

      const params = { addressId: normalizedAddressId };
      if (customerId) {
        params.customerId = customerId;
      }

      const res = await callApi({
        key: "SET_CUSTOMER_ADDRESS_PRIMARY",
        method: "PUT",
        params,
        body: { primary: true },
        auth: true,
      });

      return { ...res.data, addressId: normalizedAddressId };
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to set default address",
      );
    }
  },
);

// Validate service availability for address
export const checkServiceAvailability = createAsyncThunk(
  "address/checkServiceability",
  async ({ latitude, longitude }, ThunkAPI) => {
    try {
      const res = await callApi({
        key: "VALIDATE_ADDRESS",
        method: "POST",
        body: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
        },
        useContent: true,
      });
      return res.data;
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to check service availability",
      );
    }
  },
);

// Sync guest address after login
export const syncGuestAddress = createAsyncThunk(
  "address/syncGuest",
  async (_, ThunkAPI) => {
    try {
      const authState = ThunkAPI.getState().auth;
      const token = authState.token;
      if (!token) return ThunkAPI.rejectWithValue("No auth token found");

      const profile = authState?.Userprofile || {};
      const customerId =
        profile?.customerId || profile?._id || profile?.id || undefined;

      // Get guest address from localStorage
      const guestAddress = localStorage.getItem("guestAddress");
      if (!guestAddress) return { synced: false };

      const addressData = JSON.parse(guestAddress);

      // Create the address in backend
      const res = await callApi({
        key: "CREATE_CUSTOMER_ADDRESS",
        method: "POST",
        body: customerId
          ? {
              id: customerId,
              customerId,
              address: addressData,
            }
          : {
              address: addressData,
            },
        auth: true,
      });

      // Clear guest address from localStorage
      localStorage.removeItem("guestAddress");

      return { ...res.data, synced: true };
    } catch (error) {
      return ThunkAPI.rejectWithValue(
        error?.message || "Failed to sync guest address",
      );
    }
  },
);

// Initial state
const initialState = {
  // Address list
  addresses: [],

  // Selected address for booking/service
  selectedAddress: null,

  // Default address
  defaultAddress: null,

  // Current location from geolocation
  currentLocation: {
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
  },

  // Service availability status
  serviceAvailability: {
    isServiceable: null,
    checkedLocation: null,
    message: null,
  },

  // Address being edited/created
  draftAddress: null,

  // UI states
  loading: {
    fetch: false,
    create: false,
    update: false,
    delete: false,
    setDefault: false,
    serviceCheck: false,
    geolocation: false,
  },

  // Error states
  errors: {
    fetch: null,
    create: null,
    update: null,
    delete: null,
    setDefault: null,
    serviceCheck: null,
    geolocation: null,
  },

  // Geolocation status
  geolocationStatus: {
    supported: true,
    permissionStatus: null, // 'granted', 'denied', 'prompt'
    error: null,
    errorCode: null,
  },

  // Modal states
  modals: {
    locationSelection: false,
    addressConfirmation: false,
    addressManagement: false,
    deleteConfirmation: false,
  },

  // Address search
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // Last successful sync timestamp
  lastSyncTimestamp: null,
};

const addressSlice = createSlice({
  name: "address",
  initialState,
  reducers: {
    // Set selected address for booking
    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
      // Persist to localStorage for page refreshes
      if (typeof window !== "undefined") {
        localStorage.setItem("selectedAddress", JSON.stringify(action.payload));
      }
    },

    // Clear selected address
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("selectedAddress");
      }
    },

    // Set current location from geolocation
    setCurrentLocation: (state, action) => {
      state.currentLocation = {
        latitude: action.payload.latitude,
        longitude: action.payload.longitude,
        accuracy: action.payload.accuracy,
        timestamp: Date.now(),
      };
    },

    // Set geolocation status
    setGeolocationStatus: (state, action) => {
      state.geolocationStatus = {
        ...state.geolocationStatus,
        ...action.payload,
      };
    },

    // Set geolocation loading
    setGeolocationLoading: (state, action) => {
      state.loading.geolocation = action.payload;
    },

    // Set geolocation error
    setGeolocationError: (state, action) => {
      state.errors.geolocation = action.payload.message;
      state.geolocationStatus.error = action.payload.message;
      state.geolocationStatus.errorCode = action.payload.code;
    },

    // Clear geolocation error
    clearGeolocationError: (state) => {
      state.errors.geolocation = null;
      state.geolocationStatus.error = null;
      state.geolocationStatus.errorCode = null;
    },

    // Set draft address (for editing/creation)
    setDraftAddress: (state, action) => {
      state.draftAddress = action.payload;
    },

    // Update draft address field
    updateDraftAddressField: (state, action) => {
      if (state.draftAddress) {
        state.draftAddress = {
          ...state.draftAddress,
          ...action.payload,
        };
      }
    },

    // Clear draft address
    clearDraftAddress: (state) => {
      state.draftAddress = null;
    },

    // Set service availability
    setServiceAvailability: (state, action) => {
      state.serviceAvailability = {
        isServiceable: action.payload.isServiceable,
        checkedLocation: action.payload.location,
        message: action.payload.message,
      };
    },

    // Clear service availability
    clearServiceAvailability: (state) => {
      state.serviceAvailability = {
        isServiceable: null,
        checkedLocation: null,
        message: null,
      };
    },

    // Toggle modals
    toggleModal: (state, action) => {
      const { modal, isOpen } = action.payload;
      if (state.modals.hasOwnProperty(modal)) {
        state.modals[modal] = isOpen;
      }
    },

    // Set search results
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },

    // Set search loading
    setSearchLoading: (state, action) => {
      state.searchLoading = action.payload;
    },

    // Set search error
    setSearchError: (state, action) => {
      state.searchError = action.payload;
    },

    // Clear search
    clearSearch: (state) => {
      state.searchResults = [];
      state.searchLoading = false;
      state.searchError = null;
    },

    // Clear all errors
    clearAllErrors: (state) => {
      state.errors = {
        fetch: null,
        create: null,
        update: null,
        delete: null,
        setDefault: null,
        serviceCheck: null,
        geolocation: null,
      };
    },

    // Hydrate from localStorage (for guest users)
    hydrateFromLocalStorage: (state) => {
      if (typeof window === "undefined") return;

      // Restore selected address
      const selectedAddress = localStorage.getItem("selectedAddress");
      if (selectedAddress) {
        try {
          state.selectedAddress = JSON.parse(selectedAddress);
        } catch (e) {
          console.error("Failed to parse selected address from localStorage");
        }
      }

      // Restore guest address
      const guestAddress = localStorage.getItem("guestAddress");
      if (guestAddress) {
        try {
          const parsed = JSON.parse(guestAddress);
          state.addresses = [parsed];
          state.selectedAddress = parsed;
        } catch (e) {
          console.error("Failed to parse guest address from localStorage");
        }
      }
    },

    // Save guest address to localStorage
    saveGuestAddress: (state, action) => {
      if (typeof window !== "undefined") {
        localStorage.setItem("guestAddress", JSON.stringify(action.payload));
        state.addresses = [action.payload];
        state.selectedAddress = action.payload;
      }
    },

    // Clear guest address
    clearGuestAddress: (state) => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("guestAddress");
      }
    },

    // Reset state
    resetAddressState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch addresses
      .addCase(fetchAddresses.pending, (state) => {
        state.loading.fetch = true;
        state.errors.fetch = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading.fetch = false;
        const addresses = action.payload.value || action.payload || [];
        state.addresses = addresses;

        // Find and set default address
        const defaultAddr = addresses.find((addr) => addr.primary);
        if (defaultAddr) {
          state.defaultAddress = defaultAddr;
          // If no selected address, use default
          if (!state.selectedAddress) {
            state.selectedAddress = defaultAddr;
            if (typeof window !== "undefined") {
              localStorage.setItem(
                "selectedAddress",
                JSON.stringify(defaultAddr),
              );
            }
          }
        }

        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading.fetch = false;
        state.errors.fetch = action.payload;
      })

      // Create address
      .addCase(createAddress.pending, (state) => {
        state.loading.create = true;
        state.errors.create = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.loading.create = false;
        const newAddress = action.payload.value || action.payload;

        // If response contains all addresses, use that
        if (Array.isArray(newAddress)) {
          state.addresses = newAddress;
        } else {
          state.addresses.push(newAddress);
        }

        // If this is the first address or marked as primary, set as default
        if (state.addresses.length === 1 || newAddress.primary) {
          state.defaultAddress = newAddress;
          state.selectedAddress = newAddress;
        }

        state.draftAddress = null;
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading.create = false;
        state.errors.create = action.payload;
      })

      // Update address
      .addCase(updateAddress.pending, (state) => {
        state.loading.update = true;
        state.errors.update = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.loading.update = false;
        const updatedAddress = action.payload.value || action.payload;

        // Update in list
        const index = state.addresses.findIndex(
          (addr) => addr.id === updatedAddress.id,
        );
        if (index !== -1) {
          state.addresses[index] = updatedAddress;
        }

        // Update selected if same
        if (state.selectedAddress?.id === updatedAddress.id) {
          state.selectedAddress = updatedAddress;
        }

        // Update default if same
        if (state.defaultAddress?.id === updatedAddress.id) {
          state.defaultAddress = updatedAddress;
        }

        state.draftAddress = null;
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.loading.update = false;
        state.errors.update = action.payload;
      })

      // Delete address
      .addCase(deleteAddress.pending, (state) => {
        state.loading.delete = true;
        state.errors.delete = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading.delete = false;
        const deletedId = action.payload.deletedId;

        // Remove from list
        state.addresses = state.addresses.filter(
          (addr) => addr.id !== deletedId,
        );

        // Clear selected if deleted
        if (state.selectedAddress?.id === deletedId) {
          state.selectedAddress =
            state.defaultAddress || state.addresses[0] || null;
        }

        // Clear default if deleted
        if (state.defaultAddress?.id === deletedId) {
          state.defaultAddress = state.addresses[0] || null;
        }
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading.delete = false;
        state.errors.delete = action.payload;
      })

      // Set default address
      .addCase(setDefaultAddress.pending, (state) => {
        state.loading.setDefault = true;
        state.errors.setDefault = null;
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.loading.setDefault = false;
        const addressId = action.payload.addressId;

        // Update primary flags
        state.addresses = state.addresses.map((addr) => ({
          ...addr,
          primary: String(addr.id || addr._id) === String(addressId),
        }));

        // Set new default
        const newDefault = state.addresses.find(
          (addr) => String(addr.id || addr._id) === String(addressId),
        );
        if (newDefault) {
          state.defaultAddress = newDefault;
        }
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.loading.setDefault = false;
        state.errors.setDefault = action.payload;
      })

      // Check service availability
      .addCase(checkServiceAvailability.pending, (state) => {
        state.loading.serviceCheck = true;
        state.errors.serviceCheck = null;
      })
      .addCase(checkServiceAvailability.fulfilled, (state, action) => {
        state.loading.serviceCheck = false;
        const result = action.payload.value || action.payload;
        state.serviceAvailability = {
          isServiceable: result.isServiceable,
          checkedLocation: {
            latitude: action.meta.arg.latitude,
            longitude: action.meta.arg.longitude,
          },
          message: result.message || null,
        };
      })
      .addCase(checkServiceAvailability.rejected, (state, action) => {
        state.loading.serviceCheck = false;
        state.errors.serviceCheck = action.payload;
        state.serviceAvailability = {
          isServiceable: false,
          checkedLocation: null,
          message: "Failed to check service availability",
        };
      })

      // Sync guest address
      .addCase(syncGuestAddress.pending, (state) => {
        state.loading.create = true;
      })
      .addCase(syncGuestAddress.fulfilled, (state, action) => {
        state.loading.create = false;
        if (action.payload.synced) {
          // Guest address was synced, will be fetched with fetchAddresses
        }
      })
      .addCase(syncGuestAddress.rejected, (state, action) => {
        state.loading.create = false;
        state.errors.create = action.payload;
      });
  },
});

export const {
  setSelectedAddress,
  clearSelectedAddress,
  setCurrentLocation,
  setGeolocationStatus,
  setGeolocationLoading,
  setGeolocationError,
  clearGeolocationError,
  setDraftAddress,
  updateDraftAddressField,
  clearDraftAddress,
  setServiceAvailability,
  clearServiceAvailability,
  toggleModal,
  setSearchResults,
  setSearchLoading,
  setSearchError,
  clearSearch,
  clearAllErrors,
  hydrateFromLocalStorage,
  saveGuestAddress,
  clearGuestAddress,
  resetAddressState,
} = addressSlice.actions;

export default addressSlice.reducer;
